"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.constructHistoricalAssetVerificationReport = exports.generateReceivableDocumentationReport = exports.executeAssetVerification = exports.createAssetDocumentationEvaluator = exports.getPartialFieldResult = exports.checkAssetDocumentationFormulaErrors = void 0;
const luxon_1 = require("luxon");
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const BASEFIELD_PREFIX = 'baseFields';
const DYNAMIC_INTERMEDIATE_PREFIX = 'dynamicIntermediateFields';
const DOCUMENTATION_PREFIX = 'documentation';
/**
 * Asset documentation evaluation formulas are evaluated against the receivable's base fields, dynamic intermediate fields, and the documentation fields. This function will check the formulas for any missing fields.
 * Note: intermediate calcs are only available in CA. these formulas are authored on tape-builder. So when authroing formulas we can only partially verify those (base fields and documentation fields)
 */
const checkAssetDocumentationFormulaErrors = ({ fieldConfigs, dynamicIntermediateFields, baseFields, documentationFields, ignoreDynamicIntermediateFields, }) => {
    // we use "." as a separator to determine baseField v/s documentation fields.
    // ensure that seperator does not already exists in baseFields.
    // I think its validated elsewhere, but does not hurt to ensure it here.
    baseFields.forEach((field) => {
        (0, common_1.finleyAssert)(!field.includes('.'), `baseField [${field}] cannot contain "."`);
    });
    dynamicIntermediateFields.forEach((field) => {
        (0, common_1.finleyAssert)(!field.includes('.'), `dynamicIntermediateField [${field}] cannot contain "."`);
    });
    const baseFieldVars = baseFields.map((key) => `${BASEFIELD_PREFIX}.${key}`);
    const dynamicFieldVars = dynamicIntermediateFields.map((key) => `${DYNAMIC_INTERMEDIATE_PREFIX}.${key}`);
    const documentationFieldVars = documentationFields.map((key) => `${DOCUMENTATION_PREFIX}.${key}`);
    const availableVariables = new Set([
        ...baseFieldVars,
        ...dynamicFieldVars,
        ...documentationFieldVars,
    ]);
    const documentationFormulaErrors = fieldConfigs.map(({ comparisonFormula: formula }) => {
        if (ignoreDynamicIntermediateFields) {
            // we want to ignore any missing errors about dynamic intermediate fields
            // if asked to ignore those. This is because we can't verify those fields
            // on tape-builder. But they should be validated on CA.
            // inspect formula variables and add any dynamic intermediate fields
            const { variables } = (0, formula_engine_1.inspectDependencies)(formula);
            variables.forEach((variable) => {
                if (variable.startsWith(DYNAMIC_INTERMEDIATE_PREFIX)) {
                    availableVariables.add(variable);
                }
            });
        }
        const { errorMessages } = (0, formula_engine_1.getFormulaErrors)(formula, availableVariables);
        return {
            errorMessages,
            formula,
        };
    });
    // filter out any formulas that have no errors
    const errors = documentationFormulaErrors.filter((error) => error.errorMessages.length > 0);
    return errors;
};
exports.checkAssetDocumentationFormulaErrors = checkAssetDocumentationFormulaErrors;
const getVerificationStatus = ({ raw, generated, fields, }) => {
    if (!raw) {
        // there was no raw document entry,
        // this means raw document was not uploaded.
        return 'Untested';
    }
    if (!generated) {
        // there was no generated document entry,
        // this mean textract or sanitize failed.
        return 'TextractFailed';
    }
    // find any field that failed.
    const failed = fields.find(({ result }) => !result || !result.verificationResult);
    if (!failed) {
        return 'Passed';
    }
    // when we edit generated records manually. we set artifactDocumentId to null.
    // this is how we know that the generated record was edited manually.
    const manualEdits = generated.artifactDocumentId === null;
    return manualEdits ? 'VerifiedFailure' : 'UnverifiedFailure';
};
// filles in the receivable field result with the receivable tape value.
const getPartialFieldResult = ({ fieldConfig, receivable, }) => {
    const [domain, fieldName] = fieldConfig.receivableResponseTapeField.split('.');
    const loanTapeValue = domain === 'baseFields'
        ? receivable.baseFields[fieldName]
        : receivable.dynamicIntermediateFields[fieldName];
    return {
        field: fieldName,
        loanTapeValue: String(loanTapeValue),
        formula: fieldConfig.comparisonFormula,
        result: null,
    };
};
exports.getPartialFieldResult = getPartialFieldResult;
const createAssetDocumentationEvaluator = (fieldConfigs) => {
    const formulaStrings = fieldConfigs.map((field) => field.comparisonFormula);
    const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, formulaStrings, undefined);
    // TODO:@asset_verification: optimze to set only variables referenced by the formula.
    const setVariables = ({ receivable, document, }) => {
        // documentation.field
        // baseFields.field
        // dynamicIntermediateFields.field
        // document has following format.
        // {
        //   "fields": [{
        //     "fieldName": "amount",
        //     "raw": "$650,000",
        //     "error": null,
        //     "value": 650000,
        //     "confidence": 96
        //   },
        //   {
        //     "fieldName": "company_name",
        //     "raw": "some_name",
        //     "error": null,
        //     "value": "some_name",
        //     "confidence": 96
        //   }]
        // }
        //
        // we only need to pick the value from this object.
        const documentationValues = document.fields.reduce((acc, fieldDocumentation) => {
            const { fieldName, value } = fieldDocumentation;
            // eslint-disable-next-line no-param-reassign
            acc[fieldName] = value;
            return acc;
        }, {});
        const inputs = {
            [BASEFIELD_PREFIX]: receivable.baseFields,
            [DYNAMIC_INTERMEDIATE_PREFIX]: receivable.dynamicIntermediateFields,
            [DOCUMENTATION_PREFIX]: documentationValues,
        };
        formulaEngine.setVariables(inputs, { flatten: true });
        const evaluateConfig = (fieldConfig) => {
            const { comparisonFormula, receivableDocumentionField } = fieldConfig;
            const verificationResult = formulaEngine.evaluateBoolean(comparisonFormula);
            const documentField = document.fields.find(({ fieldName }) => fieldName === receivableDocumentionField);
            (0, common_1.finleyAssert)(documentField, `Unable to find document field: ${receivableDocumentionField}`);
            const fieldResult = {
                docSanitizedValue: String(documentField.value),
                docRawValue: documentField.raw || 'not_available',
                verificationResult,
            };
            return Object.assign(Object.assign({}, (0, exports.getPartialFieldResult)({ fieldConfig, receivable })), { result: fieldResult });
        };
        return {
            evaluateConfig,
        };
    };
    return {
        setVariables,
    };
};
exports.createAssetDocumentationEvaluator = createAssetDocumentationEvaluator;
// executes given receivables against given documentation and generates results.
const executeAssetVerification = ({ testReceivables, assetVerificationConfig, otherAttributes = {}, }) => __awaiter(void 0, void 0, void 0, function* () {
    const { setVariables } = (0, exports.createAssetDocumentationEvaluator)(assetVerificationConfig.fields);
    const results = testReceivables.map(({ receivable, latestGeneratedDocumentation }) => {
        (0, common_1.finleyAssert)(finley_types_1.typeguards.assetVerification.isAssetVerificationStoredDocument(latestGeneratedDocumentation.document), `executeAssetVerification: isAssetVerificationStoredDocument failed for [externalReceivableId:${receivable.externalReceivableId}]`);
        // known issue with our CI compilation
        // ts type checks behaves differently than in local environment
        // here we already use typeguard above to ensure that document is of type AssetVerification.StoredDocumentation.Document, but found consistent failure in CI run.
        const document = latestGeneratedDocumentation.document;
        const evaluator = setVariables({ receivable, document });
        const fields = assetVerificationConfig.fields.map((fieldConfig) => {
            return evaluator.evaluateConfig(fieldConfig);
        });
        const verificationResult = {
            schemaVersion: 1,
            fields,
        };
        const passed = fields.every((field) => field.result && field.result.verificationResult);
        const result = Object.assign(Object.assign({}, otherAttributes), { verificationResult,
            passed, generatedDocumentationId: latestGeneratedDocumentation.id, finleyReceivableId: receivable.finleyReceivableId });
        return result;
    });
    return results;
});
exports.executeAssetVerification = executeAssetVerification;
const findReceivableDocumentation = (finleyReceivableId, documentationReceivables) => {
    const matchingDocumentation = documentationReceivables.find((doc) => doc.finleyReceivableId === finleyReceivableId);
    (0, common_1.finleyAssert)(matchingDocumentation, `findReceivableDocumentation: receivable not found. [finleyReceivableId: ${finleyReceivableId}]`);
    const { documentations: internalDocumentations } = matchingDocumentation;
    // amoung given documents find latest raw.
    const latestDocumentation = internalDocumentations.length > 0
        ? internalDocumentations.reduce((acc, curr) => {
            return acc.raw.created_at > curr.raw.created_at ? acc : curr;
        }, internalDocumentations[0])
        : null;
    // find latest generated document attached to latestRaw
    const latestGeneratedDocumentation = latestDocumentation
        ? latestDocumentation.latestGenerated
        : null;
    // we need to return all documentations with timestamps for caller to render.
    // prepare that for output.
    const documentations = internalDocumentations.map(({ raw }) => {
        const generatedDocumentation = latestGeneratedDocumentation &&
            raw.id === latestGeneratedDocumentation.id
            ? {
                id: latestGeneratedDocumentation.id,
                timestamp: latestGeneratedDocumentation.created_at.getTime(),
                note: latestGeneratedDocumentation.note,
            }
            : null;
        const documentation = {
            rawId: raw.id,
            timestamp: raw.created_at.getTime(),
            generatedDocumentation,
        };
        return documentation;
    });
    return {
        documentations,
        latestGeneratedDocumentation,
        latestRawDocumentation: (latestDocumentation === null || latestDocumentation === void 0 ? void 0 : latestDocumentation.raw) || null,
    };
};
// TODO:@asset_verification: remove this method used by finleyCore,
// once we start using results from tables generated by BBPL.
const generateReceivableDocumentationReport = ({ receivables, assetVerificationConfig, documentationReceivables, reportingDate, }) => __awaiter(void 0, void 0, void 0, function* () {
    // some receivables will have documentation and some wont.
    const nonExecutedResults = [];
    const testReceivables = receivables
        .map((receivable) => {
        const { latestGeneratedDocumentation, latestRawDocumentation, } = findReceivableDocumentation(receivable.finleyReceivableId, documentationReceivables);
        if (!latestRawDocumentation || !latestGeneratedDocumentation) {
            const fields = assetVerificationConfig.fields.map((fieldConfig) => (0, exports.getPartialFieldResult)({ fieldConfig, receivable }));
            nonExecutedResults.push({
                finleyReceivableId: receivable.finleyReceivableId,
                generatedDocumentationId: 'not_available',
                verificationResult: {
                    schemaVersion: 1,
                    fields,
                },
                passed: false,
            });
            return null;
        }
        return {
            receivable,
            latestGeneratedDocumentation,
        };
    })
        .filter(common_1.typeguards.isNotNullOrUndefined);
    const executedResultsPrelim = yield (0, exports.executeAssetVerification)({
        testReceivables,
        assetVerificationConfig,
    });
    const allResults = [...executedResultsPrelim, ...nonExecutedResults];
    const reportReceivables = allResults.map((executedResult) => {
        var _a;
        const { finleyReceivableId, verificationResult: { fields }, } = executedResult;
        const { documentations, latestRawDocumentation, latestGeneratedDocumentation, } = findReceivableDocumentation(finleyReceivableId, documentationReceivables);
        const verificationStatus = getVerificationStatus({
            raw: latestRawDocumentation,
            generated: latestGeneratedDocumentation,
            fields,
        });
        const externalReceivableId = ((_a = receivables.find((receivable) => receivable.finleyReceivableId === finleyReceivableId)) === null || _a === void 0 ? void 0 : _a.externalReceivableId) || 'not_available';
        return {
            finleyReceivableId,
            externalReceivableId,
            documentations,
            verificationStatus,
            fields,
        };
    });
    const baseFieldConfigs = assetVerificationConfig.fields.map((fieldConfig) => {
        const [, fieldName] = fieldConfig.receivableResponseTapeField.split('.');
        return {
            field: fieldName,
            type: 'string',
        };
    });
    const report = {
        reportingDate,
        receivables: reportReceivables,
        baseFieldConfigs,
    };
    (0, common_1.finleyAssert)(finley_types_1.typeguards.assetVerification.isAssetVerificationReport(report), 'generateReceivableDocumentationReport:Expected asset verification report to be valid');
    return report;
});
exports.generateReceivableDocumentationReport = generateReceivableDocumentationReport;
const constructHistoricalAssetVerificationReport = ({ receivables, receivableDocumentationReport, }) => __awaiter(void 0, void 0, void 0, function* () {
    const receivableVerificationResultMap = {};
    receivableDocumentationReport.receivables.forEach((receivable) => {
        receivableVerificationResultMap[receivable.finleyReceivableId] =
            receivable.verificationStatus;
    });
    const historicalAssetVerificationDataMap = {};
    receivables.forEach((receivable) => {
        var _a, _b, _c;
        (0, common_1.finleyAssert)((_a = receivable.assignmentFields) === null || _a === void 0 ? void 0 : _a.assignedAt, new common_1.error.FinleyError('Expected receivable to have non-null assignedAt to construct historical asset verification data', {
            metadata: {
                logMeta: {
                    receivable,
                },
            },
        }));
        const assignedAtDate = common_1.date.datetimeToFullDateStr(luxon_1.DateTime.fromISO(receivable.assignmentFields.assignedAt));
        const historicalData = (_b = historicalAssetVerificationDataMap[assignedAtDate]) !== null && _b !== void 0 ? _b : {
            assignedAtDate,
            totalAssignedAtExposure: 0,
            totalUntestedAssignedAtExposure: 0,
            totalUnverifiedFailureAssignedAtExposure: 0,
            totalVerifiedFailureAssignedAtExposure: 0,
            totalVerifiedPassAssignedAtExposure: 0,
            totalReceivableCount: 0,
            verifiedPassReceivableCount: 0,
            untestedReceivableCount: 0,
            unverifiedFailureReceivableCount: 0,
            verifiedFailureReceivableCount: 0,
        };
        // 3/29/24: purchasePrice is used as proxy for exposure at time of assignment,
        // currently it is always set to that; revisit this if that changes
        const receivableAssignmentExposure = (_c = receivable.assignmentFields.purchasePrice) !== null && _c !== void 0 ? _c : 0;
        historicalData.totalAssignedAtExposure += receivableAssignmentExposure;
        historicalData.totalReceivableCount += 1;
        const verificationResult = receivableVerificationResultMap[receivable.finleyReceivableId];
        (0, common_1.finleyAssert)(verificationResult, new common_1.error.FinleyError('Expected asset verification result to exist for receivable', {
            metadata: {
                logMeta: {
                    receivable,
                },
            },
        }));
        switch (verificationResult) {
            case 'Untested':
                historicalData.totalUntestedAssignedAtExposure += receivableAssignmentExposure;
                historicalData.untestedReceivableCount += 1;
                break;
            case 'Passed':
                historicalData.totalVerifiedPassAssignedAtExposure += receivableAssignmentExposure;
                historicalData.verifiedPassReceivableCount += 1;
                break;
            case 'TextractFailed':
                historicalData.totalUntestedAssignedAtExposure += receivableAssignmentExposure;
                historicalData.untestedReceivableCount += 1;
                break;
            case 'UnverifiedFailure':
                historicalData.totalUnverifiedFailureAssignedAtExposure += receivableAssignmentExposure;
                historicalData.unverifiedFailureReceivableCount += 1;
                break;
            case 'VerifiedFailure':
                historicalData.totalVerifiedFailureAssignedAtExposure += receivableAssignmentExposure;
                historicalData.verifiedFailureReceivableCount += 1;
                break;
            default:
                throw (0, common_1.finleyAssertUnreachable)(verificationResult);
        }
        historicalAssetVerificationDataMap[assignedAtDate] = historicalData;
    });
    return Object.values(historicalAssetVerificationDataMap);
});
exports.constructHistoricalAssetVerificationReport = constructHistoricalAssetVerificationReport;
//# sourceMappingURL=assetDocumentationEvaluator.js.map